import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { Network, Database, Webhook, CheckCircle2, XCircle, RefreshCw, Eye, Plus, Trash2, Edit, Play, Pause, ChevronDown, ChevronRight, ChevronLeft } from 'lucide-react';

export default function DataPush() {
    const [activeTab, setActiveTab] = useState('configs'); // configs, logs
    const [configs, setConfigs] = useState([]);
    const [summaries, setSummaries] = useState([]);
    const [expandedTaskIds, setExpandedTaskIds] = useState([]);
    const [taskLogs, setTaskLogs] = useState({});
    const [logPages, setLogPages] = useState({});
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [testingConnection, setTestingConnection] = useState(false);
    
    // Modal states
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState({
        name: '', type: 'WEBHOOK_JSON', targetOwner: '', 
        credentials: '{}', triggerRules: '{"owners":["all"],"fetchModes":["api"],"engines":["all"]}'
    });

    const [showPayloadModal, setShowPayloadModal] = useState(false);
    const [currentPayload, setCurrentPayload] = useState('');

    useEffect(() => {
        if(activeTab === 'configs') fetchConfigs();
        else fetchLogs();
    }, [activeTab]);

    const fetchConfigs = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/data-push/configs`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            if(res.data.success) setConfigs(res.data.data);
        } catch(e) {}
        setLoading(false);
    };

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/data-push/logs/summary`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            if(res.data.success) setSummaries(res.data.data);
        } catch(e) {}
        setLoading(false);
    };

    const fetchTaskLogs = async (taskId, configId, page = 1) => {
        const key = `${taskId}-${configId}`;
        try {
            const res = await axios.get(`${API_URL}/data-push/logs?taskId=${taskId}&configId=${configId}&page=${page}&limit=10`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            if(res.data.success) {
                setTaskLogs(prev => ({ ...prev, [key]: res.data.data }));
                setLogPages(prev => ({ ...prev, [key]: { page: res.data.page, total: res.data.total } }));
            }
        } catch(e) {}
    };

    const toggleExpand = (taskId, configId) => {
        const key = `${taskId}-${configId}`;
        if (expandedTaskIds.includes(key)) {
            setExpandedTaskIds(expandedTaskIds.filter(id => id !== key));
        } else {
            setExpandedTaskIds([...expandedTaskIds, key]);
            if (!taskLogs[key]) {
                fetchTaskLogs(taskId, configId, 1);
            }
        }
    };

    
    const testConnection = async () => {
        try {
            JSON.parse(formData.credentials);
        } catch(e) {
            alert('连接配置的 JSON 格式不正确');
            return;
        }
        setTestingConnection(true);
        try {
            const res = await axios.post(`${API_URL}/data-push/test-connection`, {
                type: formData.type,
                credentials: formData.credentials
            }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            
            if (res.data.success) {
                alert('✅ ' + res.data.message);
            } else {
                alert('❌ 连接失败: ' + res.data.message);
            }
        } catch(e) {
            alert('❌ 连接失败: ' + (e.response?.data?.message || '网络异常'));
        }
        setTestingConnection(false);
    };

    const saveConfig = async () => {
        try {
            let creds = JSON.parse(formData.credentials);
            let rules = JSON.parse(formData.triggerRules);
            if (editId) {
                await axios.put(`${API_URL}/data-push/configs/${editId}`, formData, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            } else {
                await axios.post(`${API_URL}/data-push/configs`, formData, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            }
            setShowConfigModal(false);
            fetchConfigs();
        } catch(e) {
            alert('JSON格式错误或保存失败');
        }
    };

    const deleteConfig = async (id) => {
        if(!confirm('确定删除吗？')) return;
        await axios.delete(`${API_URL}/data-push/configs/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        fetchConfigs();
    };

    const retryLog = async (id, taskId, configId) => {
        await axios.post(`${API_URL}/data-push/logs/${id}/retry`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if(taskId && configId) fetchTaskLogs(taskId, configId, logPages[`${taskId}-${configId}`]?.page || 1); else fetchLogs();
    };

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Network className="text-blue-500" /> 第三方数据分发 (Data Push)
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">配置数据双写规则与外部系统直连</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 dark:border-slate-700">
                <button onClick={() => setActiveTab('configs')} className={`px-4 py-2 font-medium text-sm ${activeTab === 'configs' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}>分发通道管理</button>
                <button onClick={() => setActiveTab('logs')} className={`px-4 py-2 font-medium text-sm ${activeTab === 'logs' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}>分发日志与监控</button>
            </div>

            {activeTab === 'configs' && (
                <div className="space-y-4">
                    <button onClick={() => {
                        setEditId(null);
                        setFormData({
                            name: '', type: 'DIRECT_MYSQL', targetOwner: 'coco.zhong', 
                            credentials: '{\n  "host": "wmptcc-prod.cv42xuue9v3r.rds.cn-northwest-1.amazonaws.com.cn",\n  "user": "geoUser",\n  "password": "RSVsDB&u",\n  "database": "GEO",\n  "table": "ai_rawdata"\n}', 
                            triggerRules: '{\n  "owners":["coco.zhong"],\n  "fetchModes":["api"],\n  "engines":["all"]\n}'
                        });
                        setShowConfigModal(true);
                    }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                        <Plus size={16} /> 新建通道
                    </button>

                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700">
                        <table className="w-full text-left text-sm text-gray-600 dark:text-slate-300">
                            <thead className="bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700">
                                <tr>
                                    <th className="px-4 py-3 font-medium text-gray-900 dark:text-white">通道名称</th>
                                    <th className="px-4 py-3 font-medium">类型</th>
                                    <th className="px-4 py-3 font-medium">触发账号</th>
                                    <th className="px-4 py-3 font-medium">状态</th>
                                    <th className="px-4 py-3 font-medium">操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {configs.map(c => (
                                    <tr key={c.id} className="border-b border-gray-100 dark:border-slate-700">
                                        <td className="px-4 py-3">{c.name}</td>
                                        <td className="px-4 py-3 flex items-center gap-1">
                                            {c.type === 'DIRECT_MYSQL' ? <Database size={14} className="text-purple-500" /> : <Webhook size={14} className="text-orange-500" />}
                                            {c.type}
                                        </td>
                                        <td className="px-4 py-3">{c.targetOwner}</td>
                                        <td className="px-4 py-3">
                                            {c.status === 'ACTIVE' ? (
                                                <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 dark:border dark:border-green-500/30 rounded-full text-xs">已启用</span>
                                            ) : c.status === 'PAUSED' ? (
                                                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400 dark:border dark:border-yellow-500/30 rounded-full text-xs">已暂停</span>
                                            ) : (
                                                <span className="px-2 py-1 bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400 dark:border dark:border-gray-500/30 rounded-full text-xs">{c.status || '未启用'}</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 flex gap-3">
                                            <button onClick={async () => {
                                                const newStatus = c.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
                                                await axios.put(`${API_URL}/data-push/configs/${c.id}`, { status: newStatus }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                                                fetchConfigs();
                                            }} title={c.status === 'ACTIVE' ? '暂停推送' : '恢复推送'} className={c.status === 'ACTIVE' ? "text-orange-500 hover:text-orange-700" : "text-green-500 hover:text-green-700"}>
                                                {c.status === 'ACTIVE' ? <Pause size={16} /> : <Play size={16} />}
                                            </button>
                                            <button onClick={() => {
                                                setEditId(c.id);
                                                setFormData({
                                                    name: c.name, type: c.type, targetOwner: c.targetOwner,
                                                    credentials: c.credentials, triggerRules: c.triggerRules
                                                });
                                                setShowConfigModal(true);
                                            }} className="text-blue-500 hover:text-blue-700"><Edit size={16} /></button>
                                            <button onClick={() => deleteConfig(c.id)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'logs' && (
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700">
                    <table className="w-full text-left text-sm text-gray-600 dark:text-slate-300">
                        <thead className="bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700">
                            <tr>
                                <th className="px-4 py-3 font-medium w-10"></th>
                                <th className="px-4 py-3 font-medium">任务批次</th>
                                <th className="px-4 py-3 font-medium">目标通道</th>
                                <th className="px-4 py-3 font-medium">推送时间</th>
                                <th className="px-4 py-3 font-medium">成功/失败/等待</th>
                            </tr>
                        </thead>
                        <tbody>
                            {summaries.map(s => (
                                <React.Fragment key={`${s.taskId}-${s.configId}`}>
                                    <tr onClick={() => toggleExpand(s.taskId, s.configId)} className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer">
                                        <td className="px-4 py-3 text-gray-400">
                                            {expandedTaskIds.includes(`${s.taskId}-${s.configId}`) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                        </td>
                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">#{s.taskId}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1">
                                                {s.configType === 'DIRECT_MYSQL' ? <Database size={14} className="text-purple-500" /> : <Webhook size={14} className="text-orange-500" />}
                                                {s.configName}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">{new Date(s.latestTime).toLocaleString()}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <span className="text-green-600 flex items-center gap-1"><CheckCircle2 size={14} /> {s.successCount}</span>
                                                {s.failedCount > 0 && <span className="text-red-500 flex items-center gap-1"><XCircle size={14} /> {s.failedCount}</span>}
                                                {s.pendingCount > 0 && <span className="text-blue-500 flex items-center gap-1"><RefreshCw size={14} className="animate-spin" /> {s.pendingCount}</span>}
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedTaskIds.includes(`${s.taskId}-${s.configId}`) && (
                                        <tr className="border-b border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-900/50">
                                            <td colSpan="5" className="p-4">
                                                <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                                    <table className="w-full text-left text-xs text-gray-600 dark:text-slate-300">
                                                        <thead className="bg-gray-100 dark:bg-slate-700">
                                                            <tr>
                                                                <th className="px-3 py-2 font-medium">结果ID</th>
                                                                <th className="px-3 py-2 font-medium">状态</th>
                                                                <th className="px-3 py-2 font-medium">更新时间</th>
                                                                <th className="px-3 py-2 font-medium">报文</th>
                                                                <th className="px-3 py-2 font-medium">操作</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(taskLogs[`${s.taskId}-${s.configId}`] || []).map(l => (
                                                                <tr key={l.id} className="border-t border-gray-100 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600">
                                                                    <td className="px-3 py-2">#{l.geoResultId}</td>
                                                                    <td className="px-3 py-2">
                                                                        <div className="flex items-center gap-1 group relative w-fit">
                                                                            {l.status === 'SUCCESS' && <CheckCircle2 size={14} className="text-green-500" />}
                                                                            {l.status === 'PENDING' && <RefreshCw size={14} className="text-blue-500 animate-spin" />}
                                                                            {l.status === 'FAILED' && <XCircle size={14} className="text-red-500" />}
                                                                            <span className={l.status === 'FAILED' ? 'text-red-500' : ''}>{l.status}</span>
                                                                            {l.status === 'FAILED' && l.errorMessage && (
                                                                                <div className="hidden group-hover:block absolute left-0 top-5 bg-red-50 text-red-700 p-2 rounded border border-red-200 z-10 w-64 shadow-lg break-words">
                                                                                    {l.errorMessage}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-3 py-2">{new Date(l.updatedAt || l.createdAt).toLocaleString()}</td>
                                                                    <td className="px-3 py-2">
                                                                        {l.payload && <button onClick={(e) => { e.stopPropagation(); setCurrentPayload(l.payload); setShowPayloadModal(true); }} className="text-gray-500 hover:text-gray-700 flex items-center gap-1"><Eye size={12} />查看</button>}
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        {l.status === 'FAILED' && <button onClick={(e) => { e.stopPropagation(); retryLog(l.id, s.taskId, s.configId); }} className="text-blue-500 flex items-center gap-1 hover:underline"><RefreshCw size={12} />重试</button>}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                    
                                                    {logPages[`${s.taskId}-${s.configId}`] && logPages[`${s.taskId}-${s.configId}`].total > 10 && (
                                                        <div className="px-4 py-2 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between bg-white dark:bg-slate-800">
                                                            <span className="text-xs text-gray-500">
                                                                共 {logPages[`${s.taskId}-${s.configId}`].total} 条记录
                                                            </span>
                                                            <div className="flex gap-1">
                                                                <button 
                                                                    disabled={logPages[`${s.taskId}-${s.configId}`].page <= 1}
                                                                    onClick={(e) => { e.stopPropagation(); fetchTaskLogs(s.taskId, s.configId, logPages[`${s.taskId}-${s.configId}`].page - 1); }}
                                                                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-50"
                                                                >
                                                                    <ChevronLeft size={16} />
                                                                </button>
                                                                <span className="text-xs px-2 py-1">
                                                                    第 {logPages[`${s.taskId}-${s.configId}`].page} 页 / 共 {Math.ceil(logPages[`${s.taskId}-${s.configId}`].total / 10)} 页
                                                                </span>
                                                                <button 
                                                                    disabled={logPages[`${s.taskId}-${s.configId}`].page >= Math.ceil(logPages[`${s.taskId}-${s.configId}`].total / 10)}
                                                                    onClick={(e) => { e.stopPropagation(); fetchTaskLogs(s.taskId, s.configId, logPages[`${s.taskId}-${s.configId}`].page + 1); }}
                                                                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-50"
                                                                >
                                                                    <ChevronRight size={16} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Config Modal */}
            {showConfigModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-[600px] shadow-2xl max-h-[90vh] overflow-y-auto">
                        <h2 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">{editId ? "编辑数据分发通道" : "新建数据分发通道"}</h2>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm mb-1 text-gray-700 dark:text-slate-300">通道名称</label>
                                <input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full border rounded p-2 text-sm bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" placeholder="例如：coco 直连 AWS 数据库" />
                            </div>
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <label className="block text-sm mb-1 text-gray-700 dark:text-slate-300">分发类型</label>
                                    <select value={formData.type} onChange={e => {
                                        const newType = e.target.value;
                                        const updates = { type: newType };
                                        if (newType === 'WEBHOOK_JSON') {
                                            updates.credentials = '{\n  "url": "https://api.example.com/webhook",\n  "method": "POST",\n  "headers": {\n    "Content-Type": "application/json",\n    "Authorization": "Bearer YOUR_TOKEN"\n  }\n}';
                                        } else {
                                            updates.credentials = '{\n  "host": "wmptcc-prod.cv42xuue9v3r.rds.cn-northwest-1.amazonaws.com.cn",\n  "user": "geoUser",\n  "password": "RSVsDB&u",\n  "database": "GEO",\n  "table": "ai_rawdata"\n}';
                                        }
                                        setFormData({...formData, ...updates});
                                    }} className="w-full border rounded p-2 text-sm bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none h-[38px]">
                                        <option value="DIRECT_MYSQL">MySQL 数据库直连</option>
                                        <option value="WEBHOOK_JSON">API JSON 推送</option>
                                    </select>
                                </div>
                                <div className="flex-1">
                                    <label className="block text-sm mb-1 text-gray-700 dark:text-slate-300">目标拥有者账号(Owner)</label>
                                    <input value={formData.targetOwner} onChange={e => setFormData({...formData, targetOwner: e.target.value})} className="w-full border rounded p-2 text-sm bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none h-[38px]" placeholder="如 coco.zhong" />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-sm mb-1 font-semibold text-blue-600 dark:text-blue-400">任务名称模板 (Job Name Template)</label>
                                <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">选填。用于动态生成 job_name，支持占位符：{"{{Category}}"} (词性), {"{{BrandName}}"} (品牌名)。例如：whole_api_思序_{"{{Category}}"}_{"{{BrandName}}"}</p>
                                <input 
                                    type="text"
                                    value={(() => {
                                        try { return JSON.parse(formData.triggerRules).jobNameTemplate || ''; } catch(e) { return ''; }
                                    })()}
                                    onChange={e => {
                                        try {
                                            const rules = JSON.parse(formData.triggerRules);
                                            rules.jobNameTemplate = e.target.value;
                                            setFormData({...formData, triggerRules: JSON.stringify(rules, null, 2)});
                                        } catch(err) {}
                                    }}
                                    placeholder="whole_api_思序_{{Category}}_{{BrandName}}"
                                    className="w-full border rounded p-2 text-sm bg-slate-50 dark:bg-slate-900 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white outline-none mb-4"
                                />
                            </div>
                            <div>
                                <label className="block text-sm mb-1 font-semibold text-blue-600 dark:text-blue-400">触发规则 (JSON)</label>
                                <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">配置哪些数据才会被推送，例如只推送 API 抓取的数据</p>
                                <textarea value={formData.triggerRules} onChange={e => setFormData({...formData, triggerRules: e.target.value})} className="w-full border rounded p-2 text-xs font-mono h-24 bg-slate-50 dark:bg-slate-900 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                            </div>

                            <div>
                                <label className="block text-sm mb-1 font-semibold text-purple-600 dark:text-purple-400">连接配置 (JSON)</label>
                                <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">{formData.type === 'DIRECT_MYSQL' ? '数据库的 Host、User、Password 及表名配置' : 'API的 URL、Method 及请求 Header 配置'}</p>
                                <textarea value={formData.credentials} onChange={e => setFormData({...formData, credentials: e.target.value})} className="w-full border rounded p-2 text-xs font-mono h-32 bg-slate-50 dark:bg-slate-900 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={testConnection} disabled={testingConnection} className="px-4 py-2 border border-green-500 text-green-600 dark:text-green-400 rounded-lg text-sm hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors mr-auto flex items-center gap-2">
                                {testingConnection ? <RefreshCw size={16} className="animate-spin" /> : <Network size={16} />}
                                测试连接
                            </button>
                            <button onClick={() => setShowConfigModal(false)} className="px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">取消</button>
                            <button onClick={saveConfig} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">{editId ? "保存修改" : "保存配置"}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Payload Modal */}
            {showPayloadModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-[800px] shadow-2xl">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">实际推送数据 (Payload Snapshot)</h2>
                            <button onClick={() => setShowPayloadModal(false)}><XCircle className="text-gray-400" /></button>
                        </div>
                        <pre className="bg-slate-900 text-green-400 p-4 rounded-lg overflow-auto max-h-[60vh] text-xs font-mono">
                            {(() => {
                                try { return JSON.stringify(JSON.parse(currentPayload), null, 2); } 
                                catch(e) { return currentPayload; }
                            })()}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
}
